import { useCallback, useEffect } from "react";
import { Navbar, NavbarItem } from "@/components/shared/navbar/Navbar";
import { posthogTrackEvent, QualityEvents } from "../../../assets/posthog";
import { Dialog } from "../../../components/shared/dialog";
import { params } from "../../../params";
import { useAuthorizedQuery } from "../../../api";
import {
  TicketExploreRow,
  printTicketId,
} from "../../../api/useExplore/Explore.model";
import {
  TicketComments,
  TicketQAScoreResponse,
  TicketScore,
} from "./TicketQAScore.model";
import SidePanel, { SidePanelTab } from "./SidePanel";
import Main, { MainTab } from "./Main";
import styles from "./TicketQAScore.module.scss";
import { useQueryClient } from "react-query";
import { TicketsList } from "./TicketsList";
import cx from "classnames";
import { TicketListItem } from "@/features/tickets-info";

const useTicketData = (ticketId?: string) => {
  return useAuthorizedQuery<TicketQAScoreResponse>({
    queryKey: `useTicketData-${ticketId}`,
    url: `${params.API_URL}/platform/sampling/tickets/${ticketId}`,
    method: "GET",
    enabled: ticketId != null,
  });
};

const useTicketHasRelease = (ticketId?: string) => {
  return useAuthorizedQuery<boolean>({
    queryKey: `useTicketHasRelease-${ticketId}`,
    url: `${params.API_URL}/platform/releases/isExist/${ticketId}`,
    method: "GET",
    enabled: ticketId != null,
  });
};

export interface TicketQAScoreProps {
  ticket?: TicketExploreRow & { ticketScore?: TicketScore[] };
  navbarItems: NavbarItem[];
  onClose: () => void;
  reloadTickets?: () => Promise<unknown>;
  allowValidateTicket?: boolean;
  viewOnly?: boolean;
  defaultTabs?: {
    main?: MainTab;
    sidePanel?: SidePanelTab;
  };
  ticketListPanel?: React.ReactNode;
  ticketsList?: TicketListItem[];
  setActiveTicketId?: (id: string) => void;
  staticComments?: TicketComments[];
}

function TicketQAScore({
  ticket,
  navbarItems,
  onClose,
  reloadTickets,
  allowValidateTicket,
  viewOnly = false,
  defaultTabs,
  ticketListPanel = false,
  ticketsList,
  setActiveTicketId = () => {},
  staticComments,
}: TicketQAScoreProps) {
  const queryClient = useQueryClient();

  // Track QA score view
  useEffect(() => {
    if (ticket?.id) {
      posthogTrackEvent({
        event: QualityEvents.QA_SCORE_VIEWED,
        properties: {
          ticketId: ticket.id,
          hasScore: !!ticket.ticketScore,
          scoreCount: ticket.ticketScore?.length || 0,
          viewOnly,
        },
      });
    }
  }, [ticket?.id, ticket?.ticketScore, viewOnly]);

  const {
    data: ticketData,
    status: ticketDataStatus,
    refetch,
  } = useTicketData(ticket?.id);
  const { data: hasRelease, status: hasReleaseStaus } = useTicketHasRelease(
    ticket?.id
  );

  const avgQaScore =
    ticketData?.ticketCalculations?.avgQaScores ?? ticket?.avgQaScore;

  const handleOpenChanged = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  if (ticket == null) {
    return <></>;
  }

  const { isValidated } = ticketData?.ticketValidation ?? {
    isValidated: false,
  };

  const comments = ticketData?.comments ?? staticComments ?? [];
  const displayId = ticket.overview?.displayId ?? ticket?.overview.id;

  const loading =
    ticketDataStatus === "loading" ||
    ticketDataStatus === "idle" ||
    hasReleaseStaus === "loading" ||
    hasReleaseStaus === "idle";

  return (
    <Dialog open onOpenChange={handleOpenChanged} loading={loading}>
      <Dialog.Content size={"large"}>
        <Dialog.Title>
          <Navbar
            variant={"lite"}
            items={[
              ...navbarItems,
              ...(displayId != null
                ? [
                    {
                      title: printTicketId(displayId),
                    },
                  ]
                : []),
            ]}
          />

          <Dialog.Close />
        </Dialog.Title>

        <div
          className={cx(styles.TicketQAScore, {
            [styles.ticketListPanel]: ticketListPanel,
          })}
        >
          {ticketListPanel && (
            <TicketsList
              activeTicketId={ticket.id}
              tickets={ticketsList || []}
              setActiveTicketId={setActiveTicketId}
            />
          )}
          <Main
            ticket={ticket}
            ticketScore={ticketData?.ticketScore || ticket?.ticketScore}
            onUpdateTicketScore={async () => {
              await queryClient.removeQueries([`useTicketData-${ticket.id}`]);
              await refetch();
              await reloadTickets?.();
            }}
            allowValidateTicket={!isValidated && allowValidateTicket}
            viewOnly={viewOnly}
            avgQAScore={avgQaScore}
            defaultTab={defaultTabs?.main}
          />

          <SidePanel
            ticket={{
              ...ticket,
              avgQaScore,
              enrichments: ticketData?.enrichments,
            }}
            comments={comments}
            hasRelease={hasRelease}
            defaultTab={defaultTabs?.sidePanel}
          />
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

export default TicketQAScore;
